import { Injectable, inject, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ServiceTimelineItem, ServiceTimelineStep } from '../models/service-timeline.model';
import { environment } from '../../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';

export interface ServiceTimelinePayload {
  tenantId: string;
  serviceDetailId: string;
}

@Injectable({ providedIn: 'root' })
export class ServiceTimelineService {
  private readonly http = inject(HttpClient);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly endpoint = `${environment.API_SERVICES}`;

  readonly currentTenant = computed(() => this.tenantUrlUtil.currentTenant());

  async getServiceTimeline(payload: ServiceTimelinePayload): Promise<ServiceTimelineItem[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<ServiceTimelineItem[]>(
          `${this.endpoint}/api/customer-care/v1/${payload.tenantId}/timeline/${payload.serviceDetailId}`
        )
      );
      return response || [];
    } catch (error) {
      return [];
    }
  }

  parseTimelineSteps(timelineItems: ServiceTimelineItem[]): {
    steps: ServiceTimelineStep[];
    isCanceled: boolean;
  } {
    if (!timelineItems || timelineItems.length === 0) {
      return { steps: [], isCanceled: false };
    }

    const sortedItems = [...timelineItems].sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    const hasCanceled = sortedItems.some((item) => {
      const status = item.status?.toUpperCase();
      return status === 'CANCELLED' || status === 'CANCELED';
    });

    const isOechsleTenant = this.currentTenant() === 'TPSA';

    const steps: ServiceTimelineStep[] = sortedItems.map((item, index) => {
      const isLast = index === sortedItems.length - 1;
      const status = item.status?.toUpperCase();
      const isCanceled = status === 'CANCELLED' || status === 'CANCELED';

      let stepState: ServiceTimelineStep['stepState'] = 'completed';
      if (isCanceled) {
        stepState = 'canceled';
      } else if (isLast && !hasCanceled) {
        stepState = 'active';
      }

      const shouldShowViewMore = isOechsleTenant
        ? false
        : (!!item.observation || !!item.subStatus);

      return {
        status: item.status,
        title: item.statusDescriptionHomologate ?? item.statusDescription,
        subStatus: item.subStatus,
        observation: item.observation,
        createdBy: item.createdBy,
        date: new Date(item.createdAt),
        stepState,
        showViewMore: shouldShowViewMore,
      };
    });

    return { steps, isCanceled: hasCanceled };
  }
}
